import React, { useEffect, useMemo } from "react";
import V2SidebarNav from "../components/V2SidebarNav";
import V2MobileBottomNav from "../components/V2MobileBottomNav";
import MobileContextHeader from "../components/mobile/MobileContextHeader";
import MobileActionTray from "../components/mobile/MobileActionTray";
import GuideStrip from "../guide/GuideStrip";
import JourneyRail from "../guide/JourneyRail";
import { useOptionalProductGuide } from "../guide/GuideContext";
import { isGuideNavAllowed } from "../guide/guideProgress";
import EnrichmentProcessingBanner from "./EnrichmentProcessingBanner";
import TrialWorkspaceBadge from "../components/TrialWorkspaceBadge";
import { useV2Theme } from "../theme/ThemeProvider";
import {
  defaultTitleForRoute,
  useMobileChrome,
} from "../mobile/MobileChromeContext";
import { v2cx } from "../theme/tokens";
import { bindShellMainKeyboardScroll } from "../utils/scrollShell";
import { V2_ROUTES } from "../state/useV2Navigation";
import { IconForRoute } from "../nav/navItems";

const V2ShellLayout = ({ route, routes, onNavigate, onSignOut, children }) => {
  const { tokens } = useV2Theme();
  const chrome = useMobileChrome();
  const { setChrome } = chrome;
  const guide = useOptionalProductGuide();
  // Soft focus: primary nav goes through the guide guard when a tour is active.
  const navigate = guide?.guardNavigate || onNavigate;
  const guideChapterId = guide?.chapter?.id || guide?.state?.chapterId || "";
  const isNavAllowed = (targetRoute) => {
    if (!guide?.focusActive) return true;
    return isGuideNavAllowed({
      chapterId: guideChapterId,
      targetRoute,
    });
  };

  // Lock document scroll while the app shell is mounted so the header/tabs
  // cannot rubber-band off-screen on iOS/Android (only <main> scrolls).
  useEffect(() => {
    document.documentElement.classList.add("dp-app-shell-lock");
    return () => {
      document.documentElement.classList.remove("dp-app-shell-lock");
    };
  }, []);

  // Document can't scroll under the shell lock — route arrow keys to <main>.
  useEffect(() => bindShellMainKeyboardScroll(), []);

  // Baseline chrome on every primary route — pages refine after this.
  // Prevents stale immersive/focus state when switching tabs.
  useEffect(() => {
    setChrome({
      chromeMode: "tabs",
      contextTitle: defaultTitleForRoute(route),
      contextSubtitle: "",
      onBack: null,
      backLabel: "",
      headerTrailing: null,
      hideShellHeader: false,
      actionTray: null,
    });
  }, [route, setChrome]);

  const mobileTitle = useMemo(
    () => chrome.contextTitle || defaultTitleForRoute(route),
    [chrome.contextTitle, route]
  );

  const showMobileHeader = !chrome.hideShellHeader;
  const showTabs = !chrome.tabsHidden;
  const isContainedRoute =
    route === "home" ||
    route === "architecture-gallery" ||
    route === "insights-inbox" ||
    route === "baseline-approvals" ||
    route === "explorer";
  // Inbox / Approvals / Map: mobile keeps page-owned scroll; desktop lets
  // <main> scroll (free-flowing page, no nested list scrollport).
  const desktopMainScrolls =
    route === "architecture-gallery" ||
    route === "insights-inbox" ||
    route === "baseline-approvals";

  return (
    <div className={`${tokens.app.root} ${tokens.app.shell}`}>
      <V2SidebarNav
        route={route}
        routes={routes}
        onNavigate={navigate}
        isNavAllowed={isNavAllowed}
      />
      <div className={tokens.app.content}>
        {/* Desktop brand header (lg+) */}
        <header className={v2cx(tokens.app.header, "hidden lg:block")}>
          <div className={tokens.app.headerBar}>
            <div className="flex min-w-0 items-center gap-2.5">
              <div className="min-w-0">
                <h1 className="truncate text-lg font-semibold tracking-tight md:text-xl">
                  DecisionPen
                </h1>
                <p className={tokens.app.headerSubtitle}>
                  Architecture Decision Workspace
                </p>
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-2">
              <TrialWorkspaceBadge />
              {routes?.includes(V2_ROUTES.settings) ? (
                <button
                  type="button"
                  onClick={() => navigate(V2_ROUTES.settings)}
                  className={v2cx(
                    tokens.button.secondary,
                    "inline-flex items-center gap-1.5",
                    route === V2_ROUTES.settings &&
                      "!border-cyan-300 !bg-cyan-50 !text-cyan-700",
                    !isNavAllowed(V2_ROUTES.settings) &&
                      "cursor-not-allowed opacity-40"
                  )}
                  title={
                    isNavAllowed(V2_ROUTES.settings)
                      ? "Personal settings"
                      : "Guided demo — finish this step before opening Settings"
                  }
                  aria-label="Personal settings"
                  aria-disabled={
                    !isNavAllowed(V2_ROUTES.settings) || undefined
                  }
                  aria-current={route === V2_ROUTES.settings ? "page" : undefined}
                >
                  <IconForRoute
                    routeKey={V2_ROUTES.settings}
                    active={route === V2_ROUTES.settings}
                    tokens={tokens}
                    className="h-4 w-4"
                  />
                  Settings
                </button>
              ) : null}
              {typeof onSignOut === "function" ? (
                <button
                  type="button"
                  onClick={onSignOut}
                  className={tokens.button.secondary}
                >
                  Sign out
                </button>
              ) : null}
            </div>
          </div>
          <JourneyRail />
          <EnrichmentProcessingBanner />
          <GuideStrip />
        </header>

        {/* Mobile / tablet context header — always in the flex stack (never document-scroll away) */}
        {showMobileHeader ? (
          <div className="lg:hidden shrink-0 z-30">
            <MobileContextHeader
              title={mobileTitle}
              subtitle={chrome.contextSubtitle}
              onBack={chrome.onBack}
              backLabel={chrome.backLabel}
              trailing={
                chrome.headerTrailing ? (
                  <>
                    <TrialWorkspaceBadge compact />
                    {chrome.headerTrailing}
                  </>
                ) : (
                  <TrialWorkspaceBadge compact />
                )
              }
              showLogo={!chrome.onBack}
            />
            <div className="max-h-40 overflow-y-auto">
              <JourneyRail />
              <EnrichmentProcessingBanner />
              <GuideStrip />
            </div>
          </div>
        ) : null}

        <main
          data-v2-shell-main
          className={
            // Contained routes fill the flex slot between header + tabs.
            // Flex column (not h-full inside padded main) avoids padding-based
            // overflow. Map/Inbox/Approvals scroll <main> on lg+ instead of a
            // nested overflow-y-auto pane (which showed a desktop scrollbar
            // even when content nearly fit).
            isContainedRoute
              ? v2cx(
                  "dp-no-x-overflow flex min-h-0 min-w-0 flex-1 flex-col overscroll-y-contain",
                  desktopMainScrolls
                    ? "overflow-y-hidden lg:overflow-y-auto"
                    : "overflow-y-hidden",
                  route === "home"
                    ? "px-0 py-0 md:px-6 md:py-8"
                    : "px-4 py-4 md:px-6 md:py-8"
                )
              : tokens.app.mainWrap
          }
        >
          <div
            className={
              isContainedRoute
                ? v2cx(
                    "flex max-w-6xl w-full min-w-0 flex-col",
                    // Desktop Inbox/Approvals: size to content so <main>
                    // scrolls only when needed.
                    desktopMainScrolls
                      ? "min-h-0 flex-1 lg:flex-none"
                      : "min-h-0 flex-1"
                  )
                : tokens.app.mainMax
            }
          >
            {children}
          </div>
        </main>

        {chrome.actionTray?.length ? (
          <MobileActionTray items={chrome.actionTray} />
        ) : null}
      </div>

      {showTabs ? (
        <V2MobileBottomNav
          route={route}
          routes={routes}
          onNavigate={navigate}
          onSignOut={onSignOut}
          isNavAllowed={isNavAllowed}
        />
      ) : null}
    </div>
  );
};

export default V2ShellLayout;
